import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Button,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Tabs,
  Tab,
  Avatar,
  Pagination,
  Grid,
  useTheme,
  useMediaQuery
} from '@mui/material';
import {
  CheckCircle,
  Cancel,
  Visibility,
  Schedule,
  Person,
  Payment,
  Chat,
  Send,
  MonetizationOn,
  Block,
  Warning,
  Done
} from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useDispatch, useSelector } from 'react-redux';
import P2PPartnerService from '../../services/p2pPartner.service';
import P2PChatDialog from './P2PChatDialog';
import { setActiveChatId, markChatAsRead } from '../../request/P2P/p2pSlice';

const P2POrderManagement = ({ partnerId }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const dispatch = useDispatch();
  
  // Get orders from user Redux store
  const userData = useSelector((state) => state.userData?.user);
  const loading = useSelector((state) => state.userData?.loading || false);
  const chatSessions = useSelector((state) => state.p2p?.chatSessions || {});
  
  const myOrders = userData?.p2p_partner_orders || [];
  
  // Debug logging
  console.log('P2POrderManagement Debug:', {
    myOrders,
    myOrdersType: typeof myOrders,
    isArray: Array.isArray(myOrders),
    length: myOrders?.length
  });
  
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [cancelReason, setCancelReason] = useState('');
  const [partnerNotes, setPartnerNotes] = useState('');
  const [chatOpen, setChatOpen] = useState(false);

  const statusFilters = ['all', 'pending', 'payment_pending', 'paid', 'completed', 'cancelled', 'disputed'];
  const statusLabels = ['All', 'Pending', 'Payment Pending', 'Paid', 'Completed', 'Cancelled', 'Disputed'];

  // Filter orders based on active tab and sort by latest first
  const filteredOrders = React.useMemo(() => {
    // Ensure myOrders is always an array
    const ordersArray = Array.isArray(myOrders) ? myOrders : [];
    
    // Sort by created_at descending (latest first)
    const sortedOrders = [...ordersArray].sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);
      return dateB - dateA; // Descending order (latest first)
    });
    
    if (statusFilters[activeTab] === 'all') {
      return sortedOrders;
    }
    return sortedOrders.filter(order => order.status === statusFilters[activeTab]);
  }, [myOrders, activeTab]);

  // Pagination logic
  const paginatedOrders = React.useMemo(() => {
    // Ensure filteredOrders is always an array
    const ordersArray = Array.isArray(filteredOrders) ? filteredOrders : [];
    const startIndex = (page - 1) * 10;
    const endIndex = startIndex + 10;
    return ordersArray.slice(startIndex, endIndex);
  }, [filteredOrders, page]);

  useEffect(() => {
    const ordersArray = Array.isArray(filteredOrders) ? filteredOrders : [];
    const totalPages = Math.ceil(ordersArray.length / 10);
    setTotalPages(totalPages);
    
    // Reset to page 1 if current page exceeds total pages
    if (page > totalPages && totalPages > 0) {
      setPage(1);
    }
  }, [filteredOrders.length, page]);

  const handleOrderAction = async (orderId, action, data = null) => {
    try {
      let result;
      
      switch (action) {
        case 'accept':
          result = await P2PPartnerService.acceptOrder(orderId);
          break;
        case 'confirm-payment':
          result = await P2PPartnerService.confirmPayment(orderId);
          break;
        case 'complete':
          result = await P2PPartnerService.completeOrder(orderId, partnerNotes);
          break;
        case 'cancel':
          result = await P2PPartnerService.cancelOrder(orderId, cancelReason);
          break;
        case 'dispute':
          result = await P2PPartnerService.createDispute(orderId, data.reason);
          break;
        default:
          throw new Error(`Unknown action: ${action}`);
      }

      if (result.success) {
        // Refresh P2P data to get updated orders
        if (window.refreshP2PData) {
          window.refreshP2PData();
        }
        setDialogOpen(false);
        setCancelReason('');
        setPartnerNotes('');
        alert(`Order ${action} successful!`);
      }
    } catch (error) {
      console.error(`Error ${action} order:`, error);
      alert(`Failed to ${action} order. Please try again.`);
    }
  };

  const openChat = (order) => {
    setSelectedOrder(order);
    setChatOpen(true);
    dispatch(setActiveChatId(order.order_id));
    dispatch(markChatAsRead(order.order_id));
  };

  const openOrderDetails = (order) => {
    setSelectedOrder(order);
    setDialogOpen(true);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'pending':
        return 'warning';
      case 'accepted':
        return 'info';
      case 'payment_pending':
        return 'secondary';
      case 'payment_confirmed':
        return 'primary';
      case 'completed':
        return 'success';
      case 'cancelled':
        return 'error';
      case 'disputed':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusText = (status) => {
    return status.split('_').map(word => 
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleString();
  };

  const canConfirmPayment = (order) => {
    // Can confirm payment when order is pending or payment_pending (for both buy and sell orders)
    return ['pending', 'payment_pending'].includes(order.status);
  };

  const canCompleteOrder = (order) => {
    // Only for sell orders that are in paid status (buy orders auto-complete)
    return order.status === 'paid' && order.type === 'sell';
  };

  const canCancelOrder = (order) => {
    return ['pending'].includes(order.status);
  };

  const canCreateDispute = (order) => {
    return ['paid', 'payment_confirmed'].includes(order.status);
  };

  const canChat = (order) => {
    return !['cancelled', 'completed'].includes(order.status);
  };

  const getAvailableActions = (order) => {
    const actions = [];
    
    if (canConfirmPayment(order)) {
      actions.push({ key: 'confirm-payment', label: 'Confirm Payment', color: 'primary', icon: <Payment /> });
    }
    
    if (canCompleteOrder(order)) {
      actions.push({ key: 'complete', label: 'Release Crypto', color: 'success', icon: <MonetizationOn /> });
    }
    
    if (canChat(order)) {
      actions.push({ key: 'chat', label: 'Chat', color: 'info', icon: <Chat /> });
    }
    
    if (canCancelOrder(order)) {
      actions.push({ key: 'cancel', label: 'Cancel', color: 'error', icon: <Cancel /> });
    }
    
    if (canCreateDispute(order)) {
      actions.push({ key: 'dispute', label: 'Dispute', color: 'warning', icon: <Warning /> });
    }
    
    return actions;
  };

  return (
    <Box>
      <Typography variant="h6" fontWeight="bold" gutterBottom>
        Order Management
      </Typography>

      {/* Status Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs
          value={activeTab}
          onChange={(e, newValue) => {
            setActiveTab(newValue);
            setPage(1);
          }}
          variant={isMobile ? "scrollable" : "standard"}
          scrollButtons="auto"
        >
          {statusLabels.map((label, index) => (
            <Tab key={index} label={label} />
          ))}
        </Tabs>
      </Box>

      {/* Orders Table */}
      <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'primary.main' }}>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Order ID</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Customer</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Type</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Amount</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Commission</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Created</TableCell>
              <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedOrders.map((order, index) => (
              <motion.tr
                key={order.id}
                component={TableRow}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.3, delay: index * 0.1 }}
                sx={{
                  '&:hover': {
                    bgcolor: 'action.hover',
                  },
                }}
              >
                <TableCell>
                  <Typography variant="body2" fontWeight="bold" color="primary">
                    {order.order_id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
                      <Person sx={{ fontSize: 18 }} />
                    </Avatar>
                    <Typography variant="body2">
                      {order.customer?.name || 'Customer'}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={order.trade_type.toUpperCase()}
                    color={order.trade_type === 'buy' ? 'success' : 'error'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="bold">
                      {order.crypto_amount} {order.cryptocurrency}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {formatCurrency(order.fiat_amount, order.fiat_currency)}
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Box>
                    <Typography variant="body2" fontWeight="bold" color="primary">
                      {formatCurrency(order.commission_amount || 0, order.fiat_currency)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {(order.commission_percentage || 0)}%
                    </Typography>
                  </Box>
                </TableCell>
                <TableCell>
                  <Chip
                    label={getStatusText(order.status)}
                    color={getStatusColor(order.status)}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  <Typography variant="body2">
                    {formatDate(order.created_at)}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                    <IconButton
                      size="small"
                      color="primary"
                      onClick={() => openOrderDetails(order)}
                      title="View Details"
                    >
                      <Visibility />
                    </IconButton>
                    
                    {getAvailableActions(order).slice(0, 2).map((action) => (
                      <Button
                        key={action.key}
                        size="small"
                        variant={action.key === 'chat' ? 'outlined' : 'contained'}
                        color={action.color}
                        startIcon={action.icon}
                        onClick={() => {
                          if (action.key === 'chat') {
                            openChat(order);
                          } else if (['cancel', 'dispute', 'complete'].includes(action.key)) {
                            openOrderDetails(order);
                          } else {
                            handleOrderAction(order.id, action.key);
                          }
                        }}
                        sx={{ 
                          minWidth: 'auto',
                          position: 'relative'
                        }}
                      >
                        {action.key === 'chat' && chatSessions[order.order_id]?.unreadCount > 0 && (
                          <Box
                            sx={{
                              position: 'absolute',
                              top: -4,
                              right: -4,
                              width: 16,
                              height: 16,
                              borderRadius: '50%',
                              bgcolor: 'error.main',
                              color: 'white',
                              fontSize: '0.7rem',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              fontWeight: 'bold'
                            }}
                          >
                            {chatSessions[order.order_id].unreadCount}
                          </Box>
                        )}
                        {isMobile ? '' : action.label}
                      </Button>
                    ))}
                    
                    {getAvailableActions(order).length > 2 && (
                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => openOrderDetails(order)}
                        sx={{ minWidth: 'auto' }}
                      >
                        More
                      </Button>
                    )}
                  </Box>
                </TableCell>
              </motion.tr>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {paginatedOrders.length === 0 && !loading && (
        <Paper sx={{ p: 4, textAlign: 'center', mt: 3 }}>
          <Schedule sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary">
            No orders found
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {activeTab === 0 ? 'No orders yet' : `No ${statusLabels[activeTab].toLowerCase()} orders`}
          </Typography>
        </Paper>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(e, newPage) => setPage(newPage)}
            color="primary"
          />
        </Box>
      )}

      {/* Order Details Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="md" fullWidth>
        {selectedOrder && (
          <>
            <DialogTitle>
              Order Details - {selectedOrder.order_id}
            </DialogTitle>
            <DialogContent>
              <Grid container spacing={3}>
                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom color="primary">
                        Order Information
                      </Typography>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Type:</Typography>
                          <Chip
                            label={selectedOrder.trade_type.toUpperCase()}
                            color={selectedOrder.trade_type === 'buy' ? 'success' : 'error'}
                            size="small"
                          />
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Crypto Amount:</Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {selectedOrder.crypto_amount} {selectedOrder.cryptocurrency}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Fiat Amount:</Typography>
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(selectedOrder.fiat_amount, selectedOrder.fiat_currency)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Rate:</Typography>
                          <Typography variant="body2">
                            {formatCurrency(selectedOrder.rate_used, selectedOrder.fiat_currency)}
                          </Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Payment Method:</Typography>
                          <Typography variant="body2">{selectedOrder.payment_method}</Typography>
                        </Box>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Typography variant="body2" color="text.secondary">Status:</Typography>
                          <Chip
                            label={getStatusText(selectedOrder.status)}
                            color={getStatusColor(selectedOrder.status)}
                            size="small"
                          />
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom color="primary">
                        Customer Information
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                        <Avatar sx={{ bgcolor: 'secondary.main' }}>
                          <Person />
                        </Avatar>
                        <Box>
                          <Typography variant="body1" fontWeight="bold">
                            {selectedOrder.customer?.name || 'Customer'}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {selectedOrder.customer?.email}
                          </Typography>
                        </Box>
                      </Box>
                      {selectedOrder.customer_notes && (
                        <Box>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Customer Notes:
                          </Typography>
                          <Typography variant="body2" sx={{ p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
                            {selectedOrder.customer_notes}
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Customer Payment Information */}
                <Grid item xs={12}>
                  <Card variant="outlined">
                    <CardContent>
                      <Typography variant="h6" gutterBottom color="primary">
                        Customer Payment Information
                      </Typography>
                      {selectedOrder.payment_details && Object.keys(selectedOrder.payment_details).length > 0 ? (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                          {Object.entries(selectedOrder.payment_details).map(([key, value]) => (
                            value && (
                              <Box key={key} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography variant="body2" color="text.secondary" sx={{ textTransform: 'capitalize', fontWeight: 'bold' }}>
                                  {key.replace(/_/g, ' ')}:
                                </Typography>
                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                  {value}
                                </Typography>
                              </Box>
                            )
                          ))}
                        </Box>
                      ) : (
                        <Box sx={{ textAlign: 'center', py: 3 }}>
                          <Payment sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                          <Typography variant="body2" color="text.secondary">
                            No payment information provided by customer
                          </Typography>
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </Grid>

                {/* Partner Notes for Completion */}
                {canCompleteOrder(selectedOrder) && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Partner Notes (Optional)"
                      multiline
                      rows={2}
                      value={partnerNotes}
                      onChange={(e) => setPartnerNotes(e.target.value)}
                      placeholder="Add any notes for the customer..."
                    />
                  </Grid>
                )}

                {/* Cancellation Reason */}
                {canCancelOrder(selectedOrder) && (
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Cancellation Reason"
                      multiline
                      rows={3}
                      value={cancelReason}
                      onChange={(e) => setCancelReason(e.target.value)}
                      placeholder="Please provide a reason for cancellation..."
                    />
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <DialogActions sx={{ flexWrap: 'wrap', gap: 1 }}>
              <Button onClick={() => setDialogOpen(false)}>
                Close
              </Button>
              
              {getAvailableActions(selectedOrder).map((action) => {
                if (action.key === 'chat') {
                  return (
                    <Button
                      key={action.key}
                      variant="outlined"
                      color={action.color}
                      startIcon={action.icon}
                      onClick={() => {
                        setDialogOpen(false);
                        openChat(selectedOrder);
                      }}
                    >
                      {action.label}
                    </Button>
                  );
                }

                if (action.key === 'cancel') {
                  return (
                    <Button
                      key={action.key}
                      variant="contained"
                      color={action.color}
                      startIcon={action.icon}
                      onClick={() => handleOrderAction(selectedOrder.id, action.key)}
                      disabled={!cancelReason.trim()}
                    >
                      {action.label}
                    </Button>
                  );
                }

                if (action.key === 'complete') {
                  return (
                    <Button
                      key={action.key}
                      variant="contained"
                      color={action.color}
                      startIcon={action.icon}
                      onClick={() => handleOrderAction(selectedOrder.id, action.key)}
                    >
                      {action.label}
                    </Button>
                  );
                }

                return (
                  <Button
                    key={action.key}
                    variant="contained"
                    color={action.color}
                    startIcon={action.icon}
                    onClick={() => handleOrderAction(selectedOrder.id, action.key)}
                  >
                    {action.label}
                  </Button>
                );
              })}
            </DialogActions>
          </>
        )}
      </Dialog>

      {/* Chat Dialog */}
      <P2PChatDialog
        open={chatOpen}
        onClose={() => {
          setChatOpen(false);
          setSelectedOrder(null);
          dispatch(setActiveChatId(null));
        }}
        order={selectedOrder}
      />
    </Box>
  );
};

export default P2POrderManagement;
